import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import {
    Alert,
    Box,
    Card,
    Collapse,
    Grid,
    Stack,
    styled,
    Typography,
} from '@mui/material';
import Rating from '@mui/material/Rating';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { isPast } from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import quickBookImage from '../../assets/quickBook.png';
import TitleCase from '../BasicComponents/TitleCase';

const LicenceTypography = styled(Typography)(({ theme }) => ({
    variant: 'subtitle1',
}));

const TeacherSectionGrid = styled(Grid)(({ theme }) => ({
    padding: theme.spacing(1),
}));

const StatusInfoAlert = styled(Alert)(() => ({
    marginBottom: '12px',
}));

const LargeProfileImg = styled('img')(() => ({
    height: 'auto',
    width: 'calc(100% - 48px)',
    maxWidth: '250px',
    margin: '12px 24px',
    borderRadius: '10px',
}));

/**
 * Component for entering in a mobile number.
 *
 * @component BookingHeader
 * @param {object} props props
 * @param {object} props.booking
 * @param {string} props.booking.status
 * @param {string} props.booking.teacherProfile
 * @param {string} props.booking.teacherFirstName
 * @param {string} props.booking.teacherLastName
 * @param {string} props.booking.teacherBio
 * @param {number} props.teacherRating
 * @param {number} props.teacherRatingAmount
 * @param {[object]} props.licenceList
 * @param {string} props.schoolState
 * @param {string} props.userType School or Teacher
 * @return (
 * <CreateBooking/>
 * )
 */
const BookingHeader = props => {
    const theme = useTheme();
    const matchesMd = useMediaQuery(theme.breakpoints.up('md'));
    const matchesXL = useMediaQuery(theme.breakpoints.up('xl'));
    const matchesXS = useMediaQuery(theme.breakpoints.only('xs'));

    const [open, setOpen] = useState(false);
    const [textHeight, setTextHeight] = useState(0);
    const [collapseHeight, setCollapseHeight] = useState(0);

    const [licenceInfo, setLicenceInfo] = useState({});

    const handleOpen = () => {
        setOpen(!open);
    };

    const changeTextHeight = useCallback(node => {
        if (node !== null) {
            console.log(
                'setting text height ',
                node.getBoundingClientRect().height
            );
            setTextHeight(node.getBoundingClientRect().height);
        } else {
            console.log('text height null');
        }
    }, []);

    const changeCollapseHeight = useCallback(node => {
        if (node !== null) {
            console.log(
                'setting collapse height ',
                node.getBoundingClientRect().height
            );

            setCollapseHeight(node.getBoundingClientRect().height);
        } else {
            console.log('collapse height null');
        }
    }, []);

    //Set licence number and state
    useEffect(() => {
        let licenceFlag = false;
        console.log('Licence list', props.licenceList);
        console.log('school state', props.schoolState);
        if (props.licenceList === undefined) return;
        const licenceList = props.licenceList.sort(
            (a, b) => b.exp.yearValue - a.exp.yearValue
        );
        for (const licence of licenceList) {
            const exp = new Date(
                licence.exp.yearValue,
                parseInt(licence.exp.monthValue)
            );
            if (licence.state === props.schoolState && !isPast(exp)) {
                setLicenceInfo({
                    number: licence.number,
                    state: licence.state,
                });
                licenceFlag = true;
                break;
            }
        }
        if (licenceFlag === false) {
            // Set latest exp
            setLicenceInfo(licenceList[0]);
        }
    }, [props.schoolState, props.licenceList]);

    return (
        <Box
            sx={{
                marginBottom: '4px',
                width: '100%',
                backgroundColor: '#FFFFFF',
                borderRadius: '4px',
                boxShadow:
                    props.border === false
                        ? ''
                        : '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
            }}
        >
            <Grid
                container
                xs={12}
                directon="column-reverse"
                alignItems="stretch"
            >
                {typeof props.booking.status !== 'undefined' ? (
                    <Grid item xs={12}>
                        <StatusInfoAlert severity="info">
                            Status: {props.booking.status}
                        </StatusInfoAlert>
                    </Grid>
                ) : (
                    <Grid item xs={12}>
                        <Box sx={{ marginTop: '24px' }}></Box>
                    </Grid>
                )}
                {(props.userType === 'School' &&
                    props.booking.type !== 'quickBook') ||
                props.userType === 'Teacher' ? (
                    <>
                        <Grid item xs={12} sm={4} textAlign="left">
                            {props.userType === 'School' ? (
                                <LargeProfileImg
                                    src={props.booking.teacherProfile}
                                    alt={
                                        'Profile Image user ' +
                                        props.booking.teacherFirstName
                                    }
                                />
                            ) : (
                                <LargeProfileImg
                                    src={props.booking.schoolLogo}
                                    alt={
                                        'School Logo: ' +
                                        props.booking.schoolName
                                    }
                                />
                            )}
                            <Grid container p={3}>
                                <Grid item xs={12}>
                                    <LicenceTypography>
                                        {props.userType === 'School' &&
                                        licenceInfo.number !== undefined
                                            ? 'Teacher ID: ' +
                                              licenceInfo.number
                                            : ''}
                                    </LicenceTypography>
                                </Grid>
                                <Grid item xs={12}>
                                    <LicenceTypography>
                                        {props.userType === 'School' &&
                                        licenceInfo.state !== undefined
                                            ? 'State: ' + licenceInfo.state
                                            : ''}
                                    </LicenceTypography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <TeacherSectionGrid item xs={12} sm={3}>
                            <TitleCase
                                variant="h5"
                                data-cy="full-name"
                                sx={{ margin: '12px', textAlign: 'left' }}
                                string={
                                    props.userType === 'School'
                                        ? `${props.booking.teacherFirstName} ${props.booking.teacherLastName}`
                                        : props.booking.schoolName
                                }
                            />
                            {typeof props.teacherRating !== 'undefined' &&
                            props.userType === 'School' ? (
                                <Box sx={{ margin: '12px' }}>
                                    {props.teacherRating && (
                                        <Rating
                                            data-cy="rating"
                                            name="simple-controlled"
                                            value={props.teacherRating}
                                            size="large"
                                            precision={0.5}
                                            disabled
                                        />
                                    )}
                                    {typeof props.teacherRatingAmount !==
                                        'undefined' && (
                                        <Typography
                                            variant="body1"
                                            data-cy="num-reviews"
                                        >
                                            {`/ ${props.teacherRatingAmount} reviews`}
                                        </Typography>
                                    )}
                                    {typeof props.phoneNumber !==
                                        'undefined' && (
                                        <Typography
                                            variant="body1"
                                            data-cy="phoneNumber"
                                            sx={{ marginTop: '12px' }}
                                        >
                                            <a
                                                href={`tel:${props.phoneNumber}`}
                                            >
                                                {props.phoneNumber}
                                            </a>
                                        </Typography>
                                    )}
                                    {typeof props.email !== 'undefined' && (
                                        <Typography
                                            variant="body1"
                                            data-cy="email"
                                            sx={{ marginTop: '12px' }}
                                        >
                                            <a href={`mailto:${props.email}`}>
                                                {props.email}
                                            </a>
                                        </Typography>
                                    )}
                                </Box>
                            ) : (
                                <Box sx={{ margin: '12px' }}>
                                    {props.userType === 'School' && (
                                        <Typography variant="body1">
                                            Rating not available
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </TeacherSectionGrid>
                        <TeacherSectionGrid item xs={12} sm={4}>
                            <div>
                                {textHeight > collapseHeight ? (
                                    <Typography
                                        variant="h6"
                                        onClick={handleOpen}
                                        sx={{ margin: '12px' }}
                                    >
                                        {props.userType === 'School'
                                            ? 'About me:'
                                            : 'School Description:'}
                                        <ExpandMoreIcon
                                            sx={{ margin: '0px 15px' }}
                                        ></ExpandMoreIcon>
                                    </Typography>
                                ) : (
                                    <Typography
                                        variant="h6"
                                        sx={{ margin: '12px' }}
                                    >
                                        {props.userType === 'School'
                                            ? 'About me:'
                                            : 'School Description:'}
                                    </Typography>
                                )}
                                <Collapse
                                    in={open}
                                    timeout="auto"
                                    collapsedSize={
                                        matchesMd
                                            ? matchesXL
                                                ? '200px'
                                                : '100px'
                                            : matchesXS
                                            ? '100px'
                                            : '50px'
                                    }
                                    ref={changeCollapseHeight}
                                >
                                    <Box
                                        variant="body2"
                                        ref={changeTextHeight}
                                        p={2}
                                    >
                                        <>
                                            {typeof props.booking.teacherBio !==
                                                'undefined' && (
                                                <>
                                                    {props.userType ===
                                                        'School' &&
                                                        props.booking.teacherBio
                                                            .split('\n')
                                                            .map((i, key) => {
                                                                return (
                                                                    <Typography
                                                                        key={
                                                                            key
                                                                        }
                                                                    >
                                                                        {i}
                                                                    </Typography>
                                                                );
                                                            })}
                                                </>
                                            )}
                                            {typeof props.booking
                                                .schoolDescription !==
                                                'undefined' && (
                                                <>
                                                    {props.userType ===
                                                        'Teacher' &&
                                                        props.booking.schoolDescription
                                                            .split('\n')
                                                            .map((i, key) => {
                                                                return (
                                                                    <Typography
                                                                        key={
                                                                            key
                                                                        }
                                                                    >
                                                                        {i}
                                                                    </Typography>
                                                                );
                                                            })}
                                                </>
                                            )}
                                        </>
                                    </Box>
                                </Collapse>

                                {textHeight > collapseHeight && (
                                    <Typography
                                        variant="button"
                                        display="block"
                                        gutterBottom
                                        onClick={handleOpen}
                                        color="primary"
                                        sx={{ margin: '12px' }}
                                    >
                                        Read {open ? 'less...' : 'more...'}
                                    </Typography>
                                )}
                            </div>
                        </TeacherSectionGrid>
                        {props.userType === 'Teacher' && (
                            <Grid item xs={12}>
                                <Stack
                                    direction="row"
                                    sx={{
                                        marginLeft: '24px',
                                        marginTop: '12px',
                                    }}
                                >
                                    <LocationOnIcon />
                                    <a
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            window.open(
                                                `https://www.google.com/maps/search/?api=1&query=` +
                                                    `${props.booking.schoolLocation
                                                        .replace(/ /g, '+')
                                                        .replace(/,/g, '%2C')
                                                        .replace(/\//g, '%2F')}`
                                            );
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            sx={{ marginBottom: '12px' }}
                                        >
                                            {props.booking.schoolLocation}
                                        </Typography>
                                    </a>
                                </Stack>
                            </Grid>
                        )}
                    </>
                ) : (
                    <>
                        {' '}
                        <Grid item xs={4} sm={2} textAlign="center">
                            <LargeProfileImg
                                src={quickBookImage}
                                alt={'Quick Booking Image '}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={6}
                            sm={8}
                            textAlign="center"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Typography>
                                This quick booking is awaiting a teachers to
                                accept
                            </Typography>
                        </Grid>
                    </>
                )}
            </Grid>
        </Box>
    );
};

export default BookingHeader;
